// Live-league hero HS4 — the Sofascore-style composite (pure).
//
// A landscape graphic for a GENUINELY gate-live league: licensed
// league badge + a generated turf backdrop + licensed player
// headshots + the real live/upcoming fixture strip. Pure (props in,
// JSX out) — the gate-truth + budget guards live upstream.
//
// HONESTY (enforced here):
//   * Rendered only for a league the gate reports live (caller maps
//     status:"live" board items) — never a dormant league.
//   * Every licensed asset has an onError → monogram fallback; we
//     never show a broken image or a fabricated face/crest.
//   * No players ⇒ the face row is omitted (no invented squad).
//   * No live/upcoming fixtures ⇒ an explicit honest line, never a
//     fake scoreline.
//   * Never a bet/stake CTA — this is a transparency surface.

import {
  leagueBadgeUrl,
  playerHeadshotUrl,
  monogram,
} from "../leagueMedia.ts";

const mono: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono, monospace)",
};

export interface HeroPlayer {
  readonly playerId: number;
  readonly playerName: string;
  readonly teamName: string | null;
}

export interface HeroFixture {
  readonly id: string;
  readonly home: string;
  readonly away: string;
  readonly homeBadge: string | null;
  readonly awayBadge: string | null;
  readonly kickoff: string;
  readonly status: "upcoming" | "live" | "settled";
  readonly score?: string | null;
}

/** Deterministic backdrop hue from the league code (no asset, no
 *  fabrication — purely cosmetic, stable per league). */
function hue(code: string): number {
  let h = 0;
  for (let i = 0; i < code.length; i += 1) {
    h = (h * 31 + code.charCodeAt(i)) % 360;
  }
  return h;
}

function MonoTile({
  label,
  size,
}: {
  label: string;
  size: number;
}): JSX.Element {
  return (
    <span
      data-testid="hero-monogram"
      style={{
        ...mono,
        width: size,
        height: size,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        background: "var(--sa-hairline, #2a2a30)",
        color: "var(--sa-fg-muted, #9a9aa2)",
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

export function LiveLeagueHero({
  leagueCode,
  leagueName,
  players,
  fixtures,
}: {
  readonly leagueCode: string;
  readonly leagueName: string;
  readonly players: readonly HeroPlayer[];
  readonly fixtures: readonly HeroFixture[];
}): JSX.Element {
  const badge = leagueBadgeUrl(leagueCode);
  const h = hue(leagueCode);
  const live = fixtures.filter((f) => f.status === "live");
  const upcoming = fixtures.filter((f) => f.status === "upcoming");
  const strip = (live.length > 0 ? live : upcoming).slice(0, 4);

  return (
    <div
      data-testid={`live-hero-${leagueCode}`}
      data-league={leagueCode}
      style={{
        position: "relative",
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid var(--sa-hairline, #2a2a30)",
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        background: `linear-gradient(135deg, hsl(${h} 38% 14%) 0%, var(--sa-bg-base, #0c0c0f) 70%)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {badge ? (
          <img
            src={badge}
            alt=""
            width={44}
            height={44}
            data-testid="hero-league-badge"
            style={{ objectFit: "contain", flexShrink: 0 }}
            onError={(e) => {
              const el = e.currentTarget;
              el.style.display = "none";
              const sib = el.nextElementSibling as HTMLElement | null;
              if (sib) sib.style.display = "inline-flex";
            }}
          />
        ) : null}
        <span style={{ display: badge ? "none" : "inline-flex" }}>
          <MonoTile label={monogram(leagueName)} size={44} />
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 20, color: "var(--sa-fg-primary, #e8e8ea)" }}>
            {leagueName}
          </span>
          <span
            style={{
              ...mono,
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--sa-accent-positive, #1fbf75)",
            }}
          >
            ● Live · gate-verified
          </span>
        </div>
      </div>

      {players.length > 0 ? (
        <div
          data-testid="hero-faces"
          style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
        >
          {players.slice(0, 6).map((p) => {
            const url = playerHeadshotUrl(p.playerId);
            return (
              <div
                key={p.playerId}
                title={p.teamName ? `${p.playerName} · ${p.teamName}` : p.playerName}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 56 }}
              >
                {url ? (
                  <img
                    src={url}
                    alt=""
                    width={44}
                    height={44}
                    data-testid={`hero-face-${p.playerId}`}
                    style={{ borderRadius: "50%", objectFit: "cover", background: "var(--sa-hairline, #2a2a30)" }}
                    onError={(e) => {
                      const el = e.currentTarget;
                      el.style.display = "none";
                      const sib = el.nextElementSibling as HTMLElement | null;
                      if (sib) sib.style.display = "inline-flex";
                    }}
                  />
                ) : null}
                <span style={{ display: url ? "none" : "inline-flex" }}>
                  <MonoTile label={monogram(p.playerName)} size={44} />
                </span>
                <span
                  style={{ ...mono, fontSize: 9, color: "var(--sa-fg-muted, #9a9aa2)", textAlign: "center", lineHeight: 1.2, maxWidth: 56, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}
                >
                  {p.playerName}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      <div data-testid="hero-strip" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {strip.length === 0 ? (
          <span style={{ ...mono, fontSize: 11, color: "var(--sa-fg-muted, #9a9aa2)" }}>
            No live or imminent fixtures right now.
          </span>
        ) : (
          strip.map((f) => (
            <div
              key={f.id}
              data-testid={`hero-fx-${f.id}`}
              style={{
                ...mono,
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--sa-fg-primary, #e8e8ea)",
                borderTop: "1px solid var(--sa-hairline, #2a2a30)",
                paddingTop: 6,
              }}
            >
              <span style={{ flex: 1, textAlign: "right" }}>{f.home}</span>
              <span
                style={{
                  color:
                    f.status === "live"
                      ? "var(--sa-accent-positive, #1fbf75)"
                      : "var(--sa-fg-muted, #9a9aa2)",
                  minWidth: 56,
                  textAlign: "center",
                }}
              >
                {f.status === "live"
                  ? (f.score ?? "LIVE")
                  : f.status === "settled"
                    ? (f.score ?? "FT")
                    : f.kickoff}
              </span>
              <span style={{ flex: 1 }}>{f.away}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
